<template>
  <div class="memory-game-container">
    <div v-if="loading" class="loading-message">Loading profiles...</div>
    <div v-else-if="error" class="error-message">
      {{ error }}
    </div>
    <div v-else-if="cards.length === 0" class="no-users-message">
      Not enough profiles with photos to start the game. Please try again later.
    </div>
    <div v-else class="game-grid">
      <div
        v-for="card in cards"
        :key="card.id"
        class="memory-card"
        :class="{ flipped: card.isFlipped, matched: card.isMatched }"
        @click="flipCard(card)"
      >
        <div class="card-inner">
          <div class="card-face card-back" />
          <div class="card-face card-front">
            <img v-if="card.type === 'image'" :src="card.value" alt="Profile" class="card-image">
            <span v-else class="card-text">{{ card.value }}</span>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showAffirmation" class="affirmation-message">
      🎉 Congratulations! You matched all pairs! 🎉
    </div>

    <div class="game-controls">
      <Button variant="primary" @click="resetGame"> Play Again </Button>
      <Button variant="secondary" @click="exitGame"> Exit to App </Button>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '../stores/user'
import { getGameImage } from '../lib/imageUtils'
import Button from './ui/Button.vue'

const router = useRouter()
const userStore = useUserStore()

const cards = ref([])
const flippedCards = ref([])
const matchedPairs = ref(0)
const gameStarted = ref(false)
const showAffirmation = ref(false)
const loading = ref(true)
const error = ref(null)

const NUM_PAIRS = 8 // For a 4x4 grid, we need 8 unique pairs

const setupGame = async () => {
  loading.value = true
  error.value = null
  cards.value = []
  flippedCards.value = []
  matchedPairs.value = 0
  gameStarted.value = false
  showAffirmation.value = false

  try {
    const allUsers = await userStore.fetchAllUsers()
    const eligibleUsers = allUsers.filter(
      user => user.name && user.profile_photos && user.profile_photos.length > 0
    )

    if (eligibleUsers.length < NUM_PAIRS) {
      error.value = `Need at least ${NUM_PAIRS} profiles with names and photos to play. Found ${eligibleUsers.length}.`
      loading.value = false
      return
    }

    // Fisher-Yates shuffle helper
    const shuffleArray = array => {
      const arr = [...array]
      for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1))
        ;[arr[i], arr[j]] = [arr[j], arr[i]]
      }
      return arr
    }

    // Randomly pick NUM_PAIRS users
    const shuffledUsers = shuffleArray(eligibleUsers).slice(0, NUM_PAIRS)

    let idCounter = 0
    const imageCards = []
    const textCards = []

    shuffledUsers.forEach(user => {
      // Prioritize the thumbnail photo, otherwise use the first available photo
      const mainPhoto =
        user.profile_photos.find(photo => photo.is_thumbnail) || user.profile_photos[0]
      const originalUrl = mainPhoto ? mainPhoto.photo_url : '/public/placeholder.png'
      const photoUrl = getGameImage(originalUrl)
      const roepnaam = user.name

      imageCards.push({
        id: idCounter++,
        value: photoUrl,
        type: 'image',
        isFlipped: false,
        isMatched: false,
        pairId: user.id
      })

      textCards.push({
        id: idCounter++,
        value: roepnaam,
        type: 'text',
        isFlipped: false,
        isMatched: false,
        pairId: user.id
      })
    })

    cards.value = shuffleArray([...imageCards, ...textCards])
    loading.value = false
    gameStarted.value = true
  } catch (err) {
    console.error('Error setting up game:', err)
    error.value = 'Failed to load user profiles. Please try again.'
    loading.value = false
  }
}

const flipCard = card => {
  if (card.isMatched || card.isFlipped || flippedCards.value.length === 2) {
    return
  }

  card.isFlipped = true
  flippedCards.value.push(card)

  if (flippedCards.value.length === 2) {
    const [card1, card2] = flippedCards.value

    if (card1.pairId === card2.pairId && card1.type !== card2.type) {
      // Match found!
      card1.isMatched = true
      card2.isMatched = true
      matchedPairs.value++
      flippedCards.value = [] // Clear flipped cards immediately on match

      if (matchedPairs.value === NUM_PAIRS) {
        showAffirmation.value = true
      }
    } else {
      // No match, flip back after a delay
      setTimeout(() => {
        card1.isFlipped = false
        card2.isFlipped = false
        flippedCards.value = []
      }, 1200) // 1.2 seconds delay
    }
  }
}

const resetGame = () => {
  setupGame()
}

const exitGame = () => {
  router.push('/') // Navigate back to home or main app view
}

onMounted(() => {
  setupGame()
})
</script>

<style scoped>
.memory-game-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: auto; /* Allow height to adjust based on content */
  background-color: var(--surface);
  padding: 20px; /* Default padding */
  font-family:
    ui-sans-serif,
    system-ui,
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    Roboto,
    'Helvetica Neue',
    Arial,
    'Noto Sans',
    sans-serif,
    'Apple Color Emoji',
    'Segoe UI Emoji',
    'Segoe UI Symbol',
    'Noto Color Emoji'; /* App font */
  color: var(--color-primary);
}

@media (max-width: 768px) {
  .memory-game-container {
    padding: 0; /* Remove padding on smaller screens */
    min-height: 100vh; /* Ensure full height on mobile */
  }
}

@media (min-width: 769px) {
  .memory-game-container {
    justify-content: flex-start; /* Align content to top on larger screens */
    padding-top: 20px; /* Add some top padding for larger screens */
  }
}

.game-header {
  margin-bottom: 20px;
}

.game-title {
  color: var(--color-accent);
  font-size: 2.8em;
  text-align: center;
  text-shadow: 2px 2px 4px var(--shadow-light);
}

.loading-message,
.error-message,
.no-users-message,
.affirmation-message {
  font-size: 1.5em;
  color: var(--color-accent);
  margin-top: 20px;
  text-align: center;
}

.error-message {
  color: var(--color-warm-accent-1); /* Red for errors */
}

.affirmation-message {
  color: hsl(
    var(--color-success-hue),
    var(--color-success-saturation),
    var(--color-success-lightness)
  ); /* Green for success */
  font-weight: bold;
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
}

.game-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-gap: 15px;
  max-width: 100%; /* Allow grid to expand to full width */
  width: min(
    100%,
    calc(100vh - 200px)
  ); /* Constrain width based on viewport height to prevent overflow */
  margin-bottom: 30px;
  perspective: 1000px; /* For 3D flip effect */
}

.memory-card {
  width: 100%;
  padding-bottom: 100%; /* Makes cards square */
  position: relative;
  cursor: pointer;
  border-radius: 10px;
  box-shadow: 0 4px 8px var(--shadow-dark);
  transition: transform 0.6s;
  transform-style: preserve-3d;
  border: 3px solid var(--color-neutral-base); /* Add border to the card itself */
}

.memory-card.flipped .card-inner {
  transform: rotateY(180deg);
}

.memory-card.matched {
  box-shadow: 0 0 15px 5px
    hsla(
      var(--color-success-hue),
      var(--color-success-saturation),
      var(--color-success-lightness),
      0.7
    ); /* Green glow for matched */
  animation: matched-glow 1s forwards;
}

@keyframes matched-glow {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.03);
  }
  100% {
    transform: scale(1);
  }
}

.card-inner {
  position: absolute;
  width: 100%;
  height: 100%;
  transform-style: preserve-3d;
  transition: transform 0.6s;
  border-radius: 10px;
}

.card-face {
  position: absolute;
  width: 100%;
  height: 100%;
  backface-visibility: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  font-weight: bold;
  font-size: 1.2em;
  text-align: center;
  padding: 10px;
  box-sizing: border-box;
}

.card-back {
  background-color: var(--color-accent); /* Base green color */
  background-image: linear-gradient(
    45deg,
    var(--shadow-dark) 25%,
    transparent 25%,
    transparent 50%,
    var(--shadow-dark) 50%,
    var(--shadow-dark) 75%,
    transparent 75%,
    transparent
  ); /* Darker green stripes */
  background-size: 20px 20px;
  border: 3px solid var(--color-neutral-base); /* Light border from image */
  color: var(--color-neutral-base); /* Text color for back of card */
  font-size: 2em;
  text-shadow: 1px 1px 2px var(--shadow-darker);
}

.card-front {
  background-color: var(--color-neutral-base);
  transform: rotateY(180deg);
  border: 1px solid var(--color-accent);
  color: var(--color-primary);
}

.card-image {
  width: 100%;
  height: 100%;
  object-fit: cover; /* Ensures image fills the card, cropping if necessary */
  border-radius: 5px;
}

.card-text {
  font-size: clamp(0.8em, 4vw, 1.5em); /* Dynamically scale text */
  color: var(--color-accent);
  word-break: break-word; /* Allow long words to break */
  overflow: hidden; /* Hide overflow */
  text-overflow: ellipsis; /* Add ellipsis for overflow */
  display: -webkit-box;
  -webkit-line-clamp: 3; /* Limit to 3 lines */
  -webkit-box-orient: vertical;
}

.game-controls {
  display: flex;
  gap: var(--space-4);
  margin-top: var(--space-5);
  justify-content: center;
}

/* Responsive adjustments */
/* Ensure 4x4 grid on all screen sizes */
@media (max-width: 768px) {
  .game-grid {
    grid-gap: 5px; /* Further reduce gap for smaller screens to maximize card size */
    /* No max-height or overflow-y needed for mobile as it's handled by min-height: 100vh on container */
  }
  .game-title {
    font-size: 2em;
  }
}

@media (max-width: 480px) {
  .game-grid {
    grid-gap: 3px; /* Minimal gap for very small screens */
  }
  .game-title {
    font-size: 1.8em;
  }
}
</style>
